// Shared API response types — mirrors the server envelope shape from
// server/HANDOFF.md §4.

export interface ApiSuccess<T> {
  success: true;
  data: T;
  message?: string;
}

export interface ApiPaginated<T> {
  success: true;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiError {
  error: string;
  code: string;
  message: string;
  requestId?: string;
  fields?: Record<string, string>;
}

export type StaffRole = 'staff' | 'manager' | 'admin' | 'weighbridge_operator';
export type AuthRealm = 'staff' | 'driver' | 'platform' | 'super';

// Wave I — super-admin auth response (POST /auth/super/login).
export interface SuperLoginResult {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
  admin: { name: string; email: string };
}

export interface UserSummary {
  id: number;
  uuid: string;
  name: string;
  email: string;
  role: StaffRole | 'driver';
  realm: AuthRealm;
  company_id: number | null;
  company_name?: string | null;
}

export interface LoginResult {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
  user: UserSummary;
}

export interface RefreshResult {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
}

export interface MeResult {
  user: UserSummary;
  url_param_secret: string;
}

// company_theme — every column on the row is delivered as a flat string.
// Keys map 1:1 to CSS custom property names by replacing underscores with
// dashes (e.g. color_btn_primary_bg → --color-btn-primary-bg).
export type CompanyTheme = Record<string, string>;

// -- Pagination meta — every paginated server response includes this shape
// inside its `data` envelope, alongside the resource array.
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// -- Driver --------------------------------------------------------------
export type DriverStatus = 'active' | 'deactivated';

export interface DriverSummary {
  /** Encrypted driver id — matches the driver-locations enc_id scheme,
   *  so it round-trips through the history endpoint. Drives the replay
   *  driver selector (see useActiveDrivers). */
  enc_id: string;
  id: number;
  uuid: string;
  name: string;
  email: string | null;
  phone: string;
  address: string | null;
  postcode: string | null;
  /** The vehicle the driver collects with (Digital Waste Tracking carrier vehicle). */
  vehicle_registration?: string | null;
  username: string;
  status: DriverStatus;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface DriverListResponse {
  drivers: DriverSummary[];
  pagination: PaginationMeta;
}

// -- Booking -------------------------------------------------------------
export type BookingStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'cancellation_requested';
export type Timeframe = 'AM' | 'AM-PM' | 'PM';
export type HighlightType = 'none' | 'full' | 'notes';

export interface BookingSummary {
  enc_id: string;
  uuid: string;
  unique_id: string;
  reference: string;
  status: BookingStatus;
  customer_id: number | null;
  customer_uuid: string | null;
  name: string;
  email: string | null;
  phone: string;
  phone2: string | null;
  address: string;
  postcode: string;
  collection_address: string | null;
  collection_postcode: string | null;
  effective_address: { address: string; postcode: string; is_collection: boolean };
  car_make_model: string;
  car_registration: string;
  service: string;
  price: number;
  product_id: number | null;
  product_name: string | null;
  source_id: number | null;
  source_name: string | null;
  driver_id: number | null;
  driver_name: string | null;
  payment_mode_id: number | null;
  payment_mode_name: string | null;
  date: string; // YYYY-MM-DD
  timeframe: Timeframe;
  notes: string | null;
  driver_notes: string | null;
  reminder_email?: boolean;
  reminder_sms?: boolean;
  email_reminder_sent?: boolean;
  email_reminder_sent_at?: string | null;
  sms_reminder_sent?: boolean;
  sms_reminder_sent_at?: string | null;
  paid: boolean;
  collected: boolean;
  highlight_type: HighlightType;
  display_order: number;
  is_multi_vehicle: boolean;
  multi_vehicle_index: number | null;
  multi_vehicle_total: number | null;
  receipt_uuid: string | null;
  receipt_unique_id: string | null;
  cancellation_reason: string | null;
  cancelled_at: string | null;
  completed_at: string | null;
  /** A driver's receipt completed this booking after it was cancelled. */
  completed_after_cancellation?: boolean;
  completed_after_cancellation_acknowledged?: boolean;
  created_at: string;
  updated_at: string;
}

export interface BookingListResponse {
  bookings: BookingSummary[];
  pagination: PaginationMeta;
}

// -- Pending -------------------------------------------------------------
export type PendingStatus = 'pending' | 'final_notice' | 'booked' | 'cancelled';

export interface PendingSummary {
  enc_id: string;
  uuid: string;
  unique_id: string;
  reference: string;
  status: PendingStatus;
  name: string;
  phone: string;
  phone2: string | null;
  email: string | null;
  address: string;
  postcode: string;
  collection_address: string | null;
  collection_postcode: string | null;
  car_make_model: string;
  car_registration: string;
  price: number | null;
  product_id: number | null;
  product_name: string | null;
  source_id: number | null;
  source_name: string | null;
  notes: string | null;
  final_notice_sent_at: string | null;
  last_followup_at: string | null;
  converted_booking: {
    uuid: string;
    unique_id: string;
    reference: string;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface PendingListResponse {
  pending_bookings: PendingSummary[];
  pagination: PaginationMeta;
}

export interface PendingNoteDto {
  note: string;
  created_by_name: string | null;
  created_at: string;
}

export interface PendingDetailResponse {
  pending_booking: PendingSummary;
  notes: PendingNoteDto[];
}

export type PendingFollowupChannel = 'email' | 'sms' | 'both';

export interface SendFollowupPayload {
  type: PendingFollowupChannel;
}

export interface SendFollowupResponse {
  success: true;
  sent_via: PendingFollowupChannel;
  email_queued: boolean;
  sms_queued: boolean;
}

export interface SendFinalNoticeResponse {
  success: true;
  sent_via: PendingFollowupChannel;
  notice_number: number;
  email_queued: boolean;
  sms_queued: boolean;
}

export type IncorrectContactReason = 'phone_incorrect' | 'email_incorrect';
export type IncorrectContactPhoneChoice = 'primary' | 'secondary' | 'both';

export interface SendIncorrectContactPayload {
  reason: IncorrectContactReason;
  /** Only used for `email_incorrect` (SMS). Defaults to primary. */
  phone_choice?: IncorrectContactPhoneChoice;
}

export interface SendIncorrectContactResponse {
  success: true;
  reason: IncorrectContactReason;
  email_queued: boolean;
  sms_queued: number;
  message: string;
}

export interface CancelPendingPayload {
  reason: string;
}

export interface CreatePendingPayload {
  name: string;
  email?: string | null;
  phone: string;
  phone2?: string | null;
  address: string;
  postcode: string;
  collection_address?: string | null;
  collection_postcode?: string | null;
  car_make_model: string;
  car_registration: string;
  price?: number | null;
  product_id?: number | null;
  source?: number | null;
  notes?: string | null;
}

export type UpdatePendingPayload = Partial<CreatePendingPayload> & {
  status?: PendingStatus;
};

// -- Diary (weekly view) -------------------------------------------------
export type DiaryNoteType = 'note' | 'alert';

export interface DiaryNoteDto {
  enc_id: string;
  uuid: string;
  driver_id: number;
  date: string; // YYYY-MM-DD
  note_text: string;
  note_type: DiaryNoteType;
  added_by: number | null;
  added_by_name: string | null;
  added_at: string;
  edited_at: string | null;
}

export interface DiaryDay {
  date: string; // YYYY-MM-DD
  day_of_week: string;
  bookings: BookingSummary[];
  notes: DiaryNoteDto[];
}

export interface DiaryWeeklyResponse {
  driver: { id: number; uuid: string; name: string };
  week_start: string;
  week_end: string;
  days: DiaryDay[];
  totals: { bookings: number; revenue: number };
}

export interface DiaryReorderItem {
  unique_id: string; // enc_id (opaque token from BookingSummary.enc_id)
  display_order: number;
}

export interface DiaryReorderInput {
  orders: DiaryReorderItem[];
}

// -- Booking detail (full read) ------------------------------------------
//
// Mirrors server `BookingDetailResponse` from
// server/src/modules/scrap/bookings/bookings.types.ts. The booking's audit
// trail comes through `booking_logs`; we surface it as `BookingLogDto[]`.
// `related_vehicles` carries the sibling vehicle rows for multi-vehicle
// bookings (empty array for single-vehicle bookings).

export interface BookingLogDto {
  uuid: string;
  action: string;
  details: string | null;
  performed_by_name: string | null;
  created_at: string;
}

export interface BookingDetailResponse {
  booking: BookingSummary;
  related_vehicles: BookingSummary[];
  logs: BookingLogDto[];
  receipt: { uuid: string; unique_id: string; enc_id: string } | null;
}

// `VehicleDetail` is a domain alias for the row shape we render in the
// detail page's vehicle cards. The server returns each vehicle as a full
// `BookingSummary`, so re-export the relevant fields under a clearer name.
export type VehicleDetail = Pick<
  BookingSummary,
  | 'enc_id'
  | 'uuid'
  | 'reference'
  | 'car_make_model'
  | 'car_registration'
  | 'product_id'
  | 'product_name'
  | 'price'
  | 'notes'
  | 'multi_vehicle_index'
  | 'multi_vehicle_total'
  | 'paid'
  | 'collected'
>;

// -- Booking create ------------------------------------------------------

export interface CreateBookingVehicleInput {
  car_make_model: string;
  car_registration: string;
  product_id: number | null;
  price: number;
  notes: string | null;
}

export interface CreateBookingInput {
  customer_id?: number | null;
  customer_uuid?: string | null;
  name: string;
  email: string | null;
  phone: string;
  phone2: string | null;
  address: string;
  postcode: string;
  collection_address: string | null;
  collection_postcode: string | null;
  date: string;
  timeframe: Timeframe;
  driver: number | null;
  source: number | null;
  payment_mode: number | null;
  vehicles: CreateBookingVehicleInput[];
  send_email?: boolean;
  send_sms?: boolean;
  reminder_email?: boolean;
  reminder_sms?: boolean;
  /** Staff acknowledged the duplicate-registration warning (server answers 409
   *  DUPLICATE_REGISTRATION without it). */
  confirm_duplicate?: boolean;
}

export interface CreateBookingResponse {
  reference: string;
  master: BookingSummary;
  vehicles: BookingSummary[];
  email_queued: boolean;
  sms_queued: boolean;
  cancellation_url: string | null;
}

// -- Booking update (PUT) ------------------------------------------------
//
// Single-vehicle edit — mirrors server `updateBookingSchema`. Every field
// is optional; the form submits only what changed.

export interface UpdateBookingInput {
  name?: string;
  email?: string | null;
  phone?: string;
  phone2?: string | null;
  address?: string;
  postcode?: string;
  collection_address?: string | null;
  collection_postcode?: string | null;
  car_make_model?: string;
  car_registration?: string;
  price?: number;
  product_id?: number | null;
  date?: string;
  timeframe?: Timeframe;
  driver?: number | null;
  source?: number | null;
  payment_mode?: number | null;
  notes?: string | null;
  driver_notes?: string | null;
  send_email?: boolean;
  send_sms?: boolean;
  reminder_email?: boolean;
  reminder_sms?: boolean;
}

// -- Booking cancel ------------------------------------------------------

export type CancelDestination = 'bin' | 'pending';

export interface CancelBookingPayload {
  /** The opaque enc_id from BookingSummary.enc_id — server expects this
   *  under the `unique_id` field name. */
  unique_id: string;
  reason: string;
  destination: CancelDestination;
}

export interface DeclineCancellationPayload {
  /** Internal staff-facing rationale, written to booking_logs.details. */
  reason: string;
  /** F3 — opt-in customer notification flags. */
  notify_email: boolean;
  notify_sms: boolean;
  /** Optional customer-facing explanation, rendered in the
   *  cancellation_declined template. Leave undefined to fall back to
   *  the template's "No reason provided" default. */
  decline_reason?: string;
}

// -- Cancellation link ---------------------------------------------------

/**
 * Response from POST /api/v1/collections/bookings/:enc_id/cancellation-link.
 * The raw `token` and `url` are returned ONCE — the server only stores the
 * SHA-256 hash, so a future GET cannot reproduce the same URL.
 */
export interface CancellationLinkGenerated {
  url: string;
  token: string;
  expires_at: string;
  used_at: string | null;
}

/**
 * Response from GET /api/v1/collections/bookings/:enc_id/cancellation-link.
 * Tells the UI whether a link is currently active for this booking but
 * does NOT return the raw URL — only an obscured fingerprint, so a UI
 * that needs a usable link must POST to issue a fresh one (which
 * invalidates any prior unused token per D2).
 */
export interface CancellationLinkStatus {
  url_obscured: string;
  expires_at: string;
  used_at: string | null;
}

// -- Booking reinstate ---------------------------------------------------

/**
 * F2 — reinstate now lets staff change date/timeframe/driver/notes at
 * the moment of reviving a cancelled booking. Each field is optional;
 * omitting one keeps the booking's existing value.
 */
export interface ReinstateBookingPayload {
  collection_date?: string;
  timeframe?: Timeframe;
  driver?: number | null;
  notes?: string | null;
  notify_email: boolean;
  notify_sms: boolean;
}

// -- Duplicate-reg check -------------------------------------------------

/**
 * Per-row duplicate match returned by GET /bookings/check-duplicate.
 * `source` distinguishes a confirmed booking from a pending enquiry so
 * the modal can label/route each match appropriately.
 */
export interface DuplicateBookingMatch {
  enc_id: string;
  reference: string;
  /** YYYY-MM-DD. Null for pending records that don't carry a collection date. */
  date: string | null;
  customer_name: string;
  status: string;
  source: 'bookings' | 'pending';
}

export interface DuplicateBookingResponse {
  duplicates: {
    bookings: DuplicateBookingMatch[];
    pending: DuplicateBookingMatch[];
  };
}

// -- Pending convert (booking promotion) ---------------------------------
//
// IMPORTANT — semantic deviation from the Wave B3 brief: the brief said
// "Convert to Receipt" and `POST /api/v1/collections/receipts`. The actual server
// `POST /api/v1/collections/pending/:enc_id/convert` promotes a pending enquiry into
// a confirmed booking (NOT a receipt). UI_AUDIT §2.7 + IMPLEMENTATION_PLAN
// §8 confirm this is the legacy "Book In" flow. Following the server.

export interface ConvertPendingPayload {
  date: string;
  timeframe: Timeframe;
  driver: number | null;
  source: number | null;
  payment_mode: number | null;
  send_email?: boolean;
  send_sms?: boolean;
  reminder_email?: boolean;
  reminder_sms?: boolean;
}

export interface ConvertPendingResponse {
  booking: {
    enc_id: string;
    uuid: string;
    unique_id: string;
    reference: string;
  };
  pending_booking: PendingSummary;
  email_queued: boolean;
  sms_queued: boolean;
}

// -- Cancellation queue list ---------------------------------------------
//
// The server has no dedicated `/cancellations` endpoint — the queue is
// `GET /api/v1/collections/bookings?status=cancellation_requested` (Pending Review
// tab) and `?status=cancelled` for the All tab. Each row carries the
// fields we need from `BookingSummary` already, so this is just a domain
// alias.

export type CancellationListItem = BookingSummary;

// -- Reference data ------------------------------------------------------

export interface ReferenceDataItem {
  id: number;
  uuid: string;
  name: string;
  display_order?: number;
  is_active?: boolean;
}

export interface ReferenceDataListResponse {
  items: ReferenceDataItem[];
}

// AM / AM-PM / PM — the only "time slot" enum the server accepts. Kept
// here so callers can iterate without re-importing the type.
export const TIME_SLOTS: readonly Timeframe[] = ['AM', 'AM-PM', 'PM'] as const;

// -- Time-slot DTO (from GET /api/v1/collections/time-slots?type=weekday|weekend) ------
export type SlotDayType = 'weekday' | 'weekend';

export interface TimeSlotDto {
  id: number;
  slot_type: SlotDayType;
  label: string;
  display_order: number;
  is_active: boolean;
}

export interface TimeSlotListResponse {
  items: TimeSlotDto[];
}

// -- Customer ---------------------------------------------------------------

export type CustomerType = 'residential' | 'commercial';
export type AccountStatus = 'active' | 'inactive' | 'suspended';
export type CustomerBranch = 'scrap' | 'skip' | 'weighbridge';

export interface CustomerSummary {
  id: number;
  uuid: string;
  customer_number: string | null;
  customer_type: CustomerType;
  title: string | null;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  name: string;
  full_name: string;
  email: string | null;
  phone: string;
  phone2: string | null;
  address: string;
  postcode: string;
  vat_number: string | null;
  payment_terms: string | null;
  credit_limit: number | null;
  account_status: AccountStatus;
  /** False when the record was quick-created at the kiosk and still lacks
   *  contact/address detail. WB ticket completion is gated on this; the
   *  customer detail page surfaces a fill-in banner while false. Optional on
   *  the client mirror so older payloads/fixtures default to "complete". */
  is_complete?: boolean;
  /** Admin-only; null for staff/manager callers. */
  account_name: string | null;
  account_number: string | null;
  sort_code: string | null;
  total_jobs: number;
  total_spent: number;
  last_job_date: string | null;
  branches: readonly CustomerBranch[];
  effective_address: { address: string; postcode: string; is_collection: boolean };
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CustomerListResponse {
  customers: CustomerSummary[];
  pagination: PaginationMeta;
}

export interface CustomerBookingHistoryItem {
  id: number;
  uuid: string;
  unique_id: string;
  reference: string;
  status: BookingStatus;
  date: string;
  price: number;
  car_make_model: string;
  car_registration: string;
  created_at: string;
}

export interface CustomerBookingHistoryResponse {
  bookings: CustomerBookingHistoryItem[];
  pagination: PaginationMeta;
}

export interface CreateCustomerInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  phone2?: string | null;
  address: string;
  postcode: string;
  customer_type?: CustomerType;
  title?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  vat_number?: string | null;
  payment_terms?: string | null;
  credit_limit?: number | null;
  account_status?: AccountStatus;
  account_name?: string | null;
  account_number?: string | null;
  sort_code?: string | null;
  branches?: CustomerBranch[];
}

export type UpdateCustomerInput = Partial<CreateCustomerInput>;

export interface MergeCustomersInput {
  source_uuid: string;
  target_uuid: string;
}

export interface DuplicateCustomerWarning {
  type: 'potential_duplicate';
  message: string;
  existing_uuid: string;
}

// -- Drafts --------------------------------------------------------------

export type DraftType = 'booking' | 'receipt' | 'roger';

export interface DraftSummary {
  enc_id: string;
  uuid: string;
  draft_type: DraftType;
  title: string;
  visibility: 'private' | 'shared';
  created_by: number;
  created_by_name: string | null;
  shared_by_name: string | null;
  shared_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DraftDetail extends DraftSummary {
  data: unknown;
  is_owner: boolean;
}

export interface DraftsListResponse {
  mine: DraftSummary[];
  shared: DraftSummary[];
}

// -- Receipt ----------------------------------------------------------------

export type ReceiptStatus = 'pending_review' | 'approved' | 'rejected' | 'archived';

export interface ReceiptPhotoDto {
  /**
   * 'customer' = the customer's stored ID / address proof shown read-only on a
   * regular-customer receipt that has no photo of its own for that slot. It
   * can't be deleted from the receipt. Absent on older responses = 'receipt'.
   */
  source?: 'receipt' | 'customer';
  enc_id: string;
  uuid: string;
  file_key: string;
  presigned_url: string | null;
  mime_type: string;
  size_bytes: number;
  caption: string | null;
  display_order: number;
  created_at: string;
}

/** One detected name discrepancy on a receipt (read-only warning). */
export interface ReceiptNameMismatchIssue {
  label: string;
  foundLabel: string;
  found: string;
  expectedLabel: string;
  expected: string;
}

export interface ReceiptNameMismatch {
  issues: ReceiptNameMismatchIssue[];
}

export interface ReceiptSummary {
  enc_id: string;
  uuid: string;
  unique_id: string;
  reference: string | null;
  status: ReceiptStatus;
  receipt_date: string;
  car_registration: string;
  car_make_model: string;
  customer_name: string;
  customer_phone: string;
  amount: number;
  payment_mode_name: string | null;
  driver_name: string | null;
  driver_uuid: string | null;
  customer_uuid: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  /** ISO timestamp when the receipt was archived; null otherwise. */
  archived_at: string | null;
  /** Display name of the staff member who created the receipt — used by
   *  the approval queue to suppress the creator's own Approve / Reject
   *  buttons. Server still enforces with a 403 as defence-in-depth. */
  created_by_name: string | null;
  /** Legacy COVD parity — true when the receipt has been sent to the
   *  customer. Drives the Sent / Unsent tabs in `/receipts`. */
  is_sent: boolean;
  sent_at: string | null;
  sent_via: 'email' | 'sms' | null;
  /** Read-only name-mismatch warning (bank account vs customer, and receipt
   *  customer vs booking). null when no discrepancy is detected. */
  name_mismatch: ReceiptNameMismatch | null;
  notes_count: number;
  /** Digital Waste Tracking state. Present only for admins while DWT is switched on. */
  dwt?: ReceiptDwtInfo | null;
  created_at: string;
  updated_at: string;
}

export interface ReceiptDwtInfo {
  /** Current submission state; null = never queued or reported. */
  status: string | null;
  /** ISO deadline for reporting; null when none applies. */
  due_by: string | null;
  due_state: 'ok' | 'due_within_24h' | 'overdue' | null;
}

export interface ReceiptDetail extends ReceiptSummary {
  car_color: string | null;
  car_year: number | null;
  car_mileage: number | null;
  chassis: string | null;
  has_mot: boolean;
  has_keys: boolean;
  has_logbook: boolean;
  customer_email: string | null;
  customer_address: string;
  customer_postcode: string;
  collection_address: string | null;
  collection_postcode: string | null;
  effective_address: string;
  cheque_number: string | null;
  account_name: string | null;
  account_number: string | null;
  sort_code: string | null;
  product_name: string | null;
  notes: string | null;
  rejection_reason: string | null;
  approved_by_name: string | null;
  rejected_by_name: string | null;
  /** Inline data URI (legacy/offline rows). NULL when the signature is key-based. */
  seller_signature: string | null;
  buyer_signature: string | null;
  /** Presigned URL for key-based signatures; NULL for inline-only rows. */
  seller_signature_url?: string | null;
  buyer_signature_url?: string | null;
  photos: ReceiptPhotoDto[];
}

export interface ReceiptListResponse {
  receipts: ReceiptSummary[];
  pagination: PaginationMeta;
}

export type BulkSendVia = 'auto' | 'email' | 'sms';

export type BulkSendSkipReason =
  | 'not_approved'
  | 'already_sent'
  | 'no_contact'
  | 'no_email'
  | 'no_phone'
  | 'duplicate_in_request'
  | 'time_budget';

export interface BulkSendOutcome {
  receipt_id: string;
  reference: string | null;
  status: 'queued' | 'skipped' | 'failed';
  via?: 'email' | 'sms';
  reason?: BulkSendSkipReason;
  error?: string;
}

export interface BulkSendResult {
  requested: number;
  queued: number;
  skipped: number;
  failed: number;
  results: BulkSendOutcome[];
  /** send-all only: unsent receipts still waiting after this batch. */
  remaining_unsent?: number;
}

/** Non-blocking notice: the same plate was receipted recently. */
export interface ReceiptDuplicateWarning {
  /** Encrypted id of the existing receipt (use in the detail-page URL). */
  existing_receipt_id: string;
  existing_reference: string | null;
  /** YYYY-MM-DD. */
  existing_receipt_date: string;
  window_days: number;
}

export interface ReceiptDetailResponse {
  receipt: ReceiptDetail;
  /** Only present on create responses. */
  duplicate_warning?: ReceiptDuplicateWarning | null;
}

export interface ReceiptCheckDuplicateResponse {
  exists: boolean;
  existing_uuid?: string;
}

export interface CreateReceiptInput {
  customer_uuid?: string | null;
  driver_uuid?: string | null;
  receipt_date?: string;
  car_registration: string;
  car_make_model: string;
  car_color?: string;
  car_year?: number;
  car_mileage?: number;
  chassis?: string;
  has_mot?: boolean;
  has_keys?: boolean;
  has_logbook?: boolean;
  customer_name: string;
  customer_email?: string | null;
  customer_phone: string;
  customer_address: string;
  customer_postcode: string;
  collection_address?: string;
  collection_postcode?: string;
  payment_mode_uuid?: string;
  product_uuid?: string;
  source_uuid?: string;
  cheque_number?: string | null;
  account_name?: string | null;
  account_number?: string | null;
  sort_code?: string | null;
  /** Staff confirmation: overwrite the customer's stored bank details with this receipt's. */
  update_customer_bank_details?: boolean;
  amount: number;
  declaration_accepted?: boolean;
  seller_signature?: string;
  buyer_signature?: string;
  notes?: string | null;
}

export type UpdateReceiptInput = Partial<
  Omit<CreateReceiptInput, 'customer_uuid' | 'driver_uuid' | 'receipt_date'>
>;

export interface CustomerBankChange {
  field_name: 'account_name' | 'account_number' | 'sort_code';
  /** Masked: account number last 4, sort code last 2. */
  old_value: string;
  new_value: string;
  source_type: string;
  changed_by: { type: 'staff' | 'driver' | null; name: string | null };
  receipt_enc_id: string;
  receipt_reference: string | null;
  created_at: string;
}

export interface CustomerBankHistoryResponse {
  changes: CustomerBankChange[];
}

export interface BankDetailsCheckInput {
  customer_uuid?: string;
  receipt_enc_id?: string;
  account_name?: string | null;
  account_number?: string | null;
  sort_code?: string | null;
}

export interface BankDetailsCheckResult {
  has_customer: boolean;
  has_stored: boolean;
  differs: boolean;
  fields: Array<'account_name' | 'account_number' | 'sort_code'>;
}

export interface RejectReceiptInput {
  reason: string;
}

// -- Message Queue -------------------------------------------------------

export interface MessageQueueRow {
  id: number;
  uuid: string;
  type: 'email' | 'sms';
  recipient: string;
  recipient_name: string | null;
  subject: string | null;
  body_preview: string;
  status: 'pending' | 'processing' | 'sent' | 'failed' | 'cancelled';
  attempts: number;
  max_attempts: number;
  created_at: string;
  sent_at: string | null;
  next_retry_at: string | null;
  error_message: string | null;
}

export interface MessageQueueSummary {
  pending: number;
  processing: number;
  sent: number;
  failed: number;
  total: number;
  last_run_at: string | null;
}

export interface MessageQueueListResponse {
  rows: MessageQueueRow[];
  pagination: { page: number; limit: number; total: number; total_pages: number };
  summary: MessageQueueSummary;
}

export interface FlushQueueResponse {
  claimed: number;
  sent: number;
  failed: number;
}

// -- Invoices (Wave D) ------------------------------------------------------

export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'overdue' | 'cancelled';
export type InvoiceUserSettableStatus = 'issued' | 'paid' | 'cancelled';

export interface InvoiceLineItemDto {
  line_number: number;
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: number;
  line_total: number;
  booking_uuid: string | null;
}

export interface InvoiceLineItemInput {
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate?: number;
  booking_uuid?: string;
}

export interface InvoiceSummary {
  enc_id: string;
  uuid: string;
  invoice_number: string;
  status: InvoiceStatus;
  customer_uuid: string | null;
  customer_name: string | null;
  issue_date: string;
  due_date: string | null;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  amount_paid: number;
  currency: string;
  paid_at: string | null;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvoiceDetail extends InvoiceSummary {
  notes: string | null;
  customer_email: string | null;
  customer_address: string | null;
  customer_postcode: string | null;
  created_by_name: string | null;
  items: InvoiceLineItemDto[];
}

export interface InvoiceListResponse {
  invoices: InvoiceSummary[];
  pagination: PaginationMeta;
}

export interface InvoiceDetailResponse {
  invoice: InvoiceDetail;
}

export interface CreateInvoiceInput {
  customer_uuid?: string;
  issue_date?: string;
  due_date?: string;
  currency?: string;
  notes?: string;
  status?: 'draft' | 'issued';
  items: InvoiceLineItemInput[];
}

export interface UpdateInvoiceInput {
  status?: InvoiceUserSettableStatus;
  notes?: string | null;
  due_date?: string | null;
}

// -- Invoices: work-sources lookup (filter dropdown) -----------------------

export interface InvoiceWorkSourceOption {
  id: number;
  name: string;
  description: string | null;
}

export interface InvoiceWorkSourcesResponse {
  work_sources: InvoiceWorkSourceOption[];
}

// -- SCC invoice compare ----------------------------------------------------

export type InvoiceCompareMatchStatus =
  | 'collected'
  | 'booked'
  | 'pending'
  | 'cancelled'
  | 'not_found';

export type InvoiceCompareOurStatus =
  | 'collected'
  | 'active'
  | 'pending'
  | 'cancelled';

export interface InvoiceCompareLineDto {
  invoice_reg: string;
  invoice_vehicle: string;
  invoice_amount: number | null;
  match_status: InvoiceCompareMatchStatus;
  our_status: InvoiceCompareOurStatus | null;
  our_customer: string | null;
  collection_date: string | null;
  driver: string | null;
  our_amount: number | null;
  has_receipt: boolean | null;
}

export interface InvoiceCompareUnmatchedInternalDto {
  registration: string;
  make_model: string | null;
  status: string;
  collected: boolean;
  source: 'booking' | 'pending';
}

export interface InvoiceCompareSummary {
  total_invoice_lines: number;
  matched: number;
  collected: number;
  cancelled: number;
  not_found: number;
}

export interface InvoiceCompareResponse {
  summary: InvoiceCompareSummary;
  lines: InvoiceCompareLineDto[];
  unmatched_internal: InvoiceCompareUnmatchedInternalDto[];
}

// -- BACS payment runs (Wave D) --------------------------------------------

export type BacsRunStatus = 'draft' | 'submitted' | 'voided';

export interface BacsRunSummary {
  enc_id: string;
  uuid: string;
  reference: string;
  status: BacsRunStatus;
  payment_date: string;
  total_amount: number;
  total_records: number;
  submitted_at: string | null;
  voided_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BacsRecordDto {
  enc_id: string;
  uuid: string;
  receipt_uuid_enc: string;
  receipt_reference: string | null;
  receipt_unique_id: string;
  receipt_car_registration: string;
  receipt_customer_name: string;
  receipt_date: string;
  payment_amount: number;
  account_name: string | null;
  account_number: string | null;
  sort_code: string | null;
}

export interface BacsRunDetail extends BacsRunSummary {
  notes: string | null;
  void_reason: string | null;
  created_by_name: string | null;
  submitted_by_name: string | null;
  voided_by_name: string | null;
  records: BacsRecordDto[];
}

export interface BacsRunListResponse {
  runs: BacsRunSummary[];
  pagination: PaginationMeta;
}

export interface BacsRunDetailResponse {
  run: BacsRunDetail;
}

export interface UnpaidReceiptDto {
  enc_id: string;
  uuid: string;
  unique_id: string;
  reference: string | null;
  receipt_date: string;
  car_registration: string;
  customer_name: string;
  amount: number;
  has_banking_details: boolean;
  payment_mode_id: number | null;
  payment_mode_name: string | null;
  /** Non-blocking: bank details differ from the customer's stored details, or this receipt
   *  updated them (audit row). Bank-transfer unpaid list only. Formerly `bank_details_differ`. */
  bank_details_changed: boolean;
  /** Amount still payable — 0 for Free Collect, whose stored `amount` is kept. */
  payable_amount: number;
}

export interface UnpaidReceiptListResponse {
  receipts: UnpaidReceiptDto[];
  pagination: PaginationMeta;
}

export interface MarkReceiptsPaidInput {
  receipt_enc_ids: string[];
  payment_mode_name: 'Cheque' | 'Bread4Scrap' | 'Free Collect';
  ticket_reference: string;
  paid_date: string;
}

export interface MarkReceiptsPaidResponse {
  updated: number;
}

export interface CreateBacsRunInput {
  payment_date: string;
  receipt_ids: string[];
  notes?: string;
}

export interface BankingDetailsMissingPayload {
  error: 'BankingDetailsMissing';
  code: 'BANKING_DETAILS_MISSING';
  message: string;
  receipts: ReadonlyArray<{
    receipt_uuid_enc: string;
    receipt_reference: string | null;
    car_registration: string;
    missing: ReadonlyArray<
      'sort_code' | 'sort_code_invalid' | 'account_number' | 'account_number_invalid' | 'account_name'
    >;
  }>;
}

export interface GenerateBacsInput {
  receipt_ids: string[];
  payment_date?: string;
}

export interface GeneratedBacsFile {
  enc_id: string;
  filename: string;
  template_name: string | null;
  run_reference: string | null;
  total_amount: number;
  total_transactions: number;
  generated_by_name: string | null;
  date_generated: string;
}

export interface GenerateBacsResult {
  run: BacsRunDetail;
  file: GeneratedBacsFile;
}

// -- QuickBooks (Wave D) ----------------------------------------------------

export type QbSyncRunStatus = 'running' | 'completed' | 'partial' | 'failed';

/** Module a QuickBooks integration surface targets. Threaded through the
 *  status / settings / sync hooks as `?module=`. Mappings + connection are
 *  tenant-global and NOT module-scoped. */
export type QbModule = 'collections' | 'weighbridge' | 'skip';

export interface QbStatusResponse {
  connected: boolean;
  realm_id: string | null;
  connected_at: string | null;
  connected_by: string | null;
  last_sync_at: string | null;
  last_sync_status: 'success' | 'partial' | 'failed' | null;
  token_expires_at: string | null;
}

export interface QbSyncErrorEntry {
  entity_type: 'customer' | 'invoice' | 'payment';
  entity_id: string;
  message: string;
  qb_error?: string;
}

export interface QbSyncRunDto {
  uuid: string;
  status: QbSyncRunStatus;
  sync_started_at: string;
  sync_completed_at: string | null;
  records_attempted: number;
  records_succeeded: number;
  records_failed: number;
  triggered_by_name: string | null;
  notes: string | null;
  error_log: ReadonlyArray<QbSyncErrorEntry>;
}

export interface QbSyncListResponse {
  runs: QbSyncRunDto[];
  pagination: PaginationMeta;
}

// -- Grouped sync (Batch 6) --------------------------------------------------

export interface QbEligibleReceipt {
  enc_id: string;
  unique_id: string;
  reference: string;
  customer_name: string | null;
  car_registration: string | null;
  amount: string;
  receipt_date: string;
  payment_mode_name: string | null;
  cheque_number: string | null;
}

export interface QbEligibleResponse {
  receipts: QbEligibleReceipt[];
}

export interface QbSyncRecord {
  receipt_enc_id: string | null;
  unique_id: string | null;
  reference: string | null;
  customer_name: string | null;
  amount: string | null;
  receipt_date: string | null;
  status: 'synced' | 'failed';
  qb_entity_id: string | null;
  qb_entity_type: string | null;
  doc_number: string | null;
  is_group: boolean;
  group_size: number;
  synced_at: string | null;
  error_message: string | null;
}

export interface QbSyncRecordsResponse {
  records: QbSyncRecord[];
  pagination: PaginationMeta;
}

export interface QbGroupSyncBody {
  receipt_enc_ids: string[];
  vendor_name?: string;
  memo?: string;
  allow_mixed_payment?: boolean;
}

export interface QbGroupSyncResult {
  run_uuid: string;
  qb_entity_id: string;
  entity_type: 'group_expense';
  doc_number: string | null;
  txn_date: string;
  total: string;
  member_count: number;
  attachments_failed: number;
  members: Array<{
    receipt_enc_id: string;
    unique_id: string;
    amount: string;
    attachment: 'attached' | 'skipped' | 'failed' | 'disabled';
  }>;
}

export interface QbAuthInitiateResponse {
  auth_url: string;
  state: string;
  expires_at: string;
}

export interface QbSyncTriggerResponse {
  run_uuid: string;
  status: QbSyncRunStatus;
  message: string;
}

// -- QuickBooks settings (Phase 2/3) ----------------------------------------

export type QbExpenseCategoryMode = 'auto' | 'manual';
export type QbPaymentTypeMode = 'proper_accounting' | 'all_expense' | 'custom';
export type QbVendorNaming = 'customer_name' | 'customer_name_vehicle' | 'customer_email';
export type QbTrimDirection = 'none' | 'start' | 'end';

export interface QbSettings {
  auto_sync: boolean;
  sync_receipts: boolean;
  attach_pdf: boolean;
  batch_size: number;
  cash_account_name: string | null;
  bank_account_name: string | null;
  expense_category_mode: QbExpenseCategoryMode;
  expense_category_name: string | null;
  income_account_name: string | null;
  enable_customer_ref: boolean;
  invoice_payment_terms: string | null;
  payment_type_mode: QbPaymentTypeMode;
  custom_payment_mapping: Record<string, string> | null;
  payment_account_mapping: Record<string, string> | null;
  vendor_naming: QbVendorNaming;
  line_description_template: string | null;
  transaction_memo_template: string | null;
  use_unique_id_as_docnumber: boolean;
  docnumber_prefix: string | null;
  docnumber_template: string | null;
  docnumber_mode_overrides: Record<string, string> | null;
  unique_id_trim_direction: QbTrimDirection;
  unique_id_trim_chars: number;
  enable_departments: boolean;
  department_name: string | null;
  enable_classes: boolean;
  class_name: string | null;
  enable_job_costing: boolean;
  mark_billable: boolean;
  enable_tax_tracking: boolean;
  tax_code: string | null;
  group_docnumber_prefix: string | null;
  group_docnumber_template: string | null;
  group_memo_template: string | null;
  group_line_template: string | null;
  group_warn_mixed_modes: boolean;
  group_attach_all_pdfs: boolean;
  group_use_earliest_date: boolean;
  group_min_transactions: number;
}

export interface QbSettingsResponse {
  settings: QbSettings;
  warnings: string[];
}

export type QbSettingsPatch = Partial<QbSettings>;

/** One active account from the connected QuickBooks company (account pickers). */
export interface QbAccountSummary {
  id: string;
  name: string;
  fully_qualified_name: string;
  account_type: string;
  account_sub_type: string | null;
}

export interface QbAccountCheck {
  setting: string;
  name: string;
  ok: boolean;
  message: string;
}

export interface QbValidateResult {
  ok: boolean;
  checks: QbAccountCheck[];
}

export type QbMappingKind = 'customer' | 'vendor';

export interface QbMappingDto {
  enc_id: string;
  kind: QbMappingKind;
  customer_uuid: string | null;
  customer_name: string;
  qb_id: string;
  created_at: string;
  updated_at: string;
}

export interface QbMappingsResponse {
  mappings: QbMappingDto[];
  pagination: PaginationMeta;
}

export interface QbMappingUpsertBody {
  kind: QbMappingKind;
  customer_uuid?: string;
  customer_name: string;
  qb_id: string;
}

// -- Activity log --------------------------------------------------------
export interface ActivityLogEntry {
  id: number;
  action: string;
  details: string | null;
  entity_type: string | null;
  entity_enc_id: string | null;
  staff_id: number | null;
  driver_id: number | null;
  ip_address: string | null;
  user_agent: string | null;
  request_id: string | null;
  created_at: string;
}

export interface ActivityLogListResponse {
  logs: ActivityLogEntry[];
  pagination: PaginationMeta;
}

// -- Users / staff (Wave E) ----------------------------------------------
export type StaffStatus = 'active' | 'deactivated';

export interface StaffSummary {
  id: number;
  uuid: string;
  name: string;
  email: string;
  phone: string | null;
  address: string | null;
  postcode: string | null;
  username: string;
  role: StaffRole;
  status: StaffStatus;
  force_password_change: boolean;
  last_login_at: string | null;
  last_password_changed_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface StaffListResponse {
  staff: StaffSummary[];
  pagination: PaginationMeta;
}

export interface CreateStaffInput {
  name: string;
  email: string;
  phone?: string | null;
  address?: string | null;
  postcode?: string | null;
  username: string;
  role: StaffRole;
  status?: StaffStatus;
  password?: string;
}

export interface UpdateStaffInput {
  name?: string;
  email?: string;
  phone?: string | null;
  address?: string | null;
  postcode?: string | null;
  role?: StaffRole;
  status?: StaffStatus;
  current_password?: string;
  new_password?: string;
}

// -- Drivers admin (Wave E) — extends DriverSummary --------------------
export interface CreateDriverInput {
  name: string;
  email?: string | null;
  phone: string;
  address?: string | null;
  postcode?: string | null;
  vehicle_registration?: string | null;
  username: string;
  password?: string;
  status?: DriverStatus;
}

export interface UpdateDriverInput {
  name?: string;
  email?: string | null;
  phone?: string;
  address?: string | null;
  postcode?: string | null;
  vehicle_registration?: string | null;
  new_password?: string;
}

// -- Message templates (Wave E) ------------------------------------------
export type TemplateChannelType = 'email' | 'sms' | 'whatsapp' | 'push';

export interface TemplateInfo {
  id: number;
  uuid: string;
  template_key: string;
  template_name: string;
  channel_type: TemplateChannelType;
  subject: string | null;
  body_html: string | null;
  body_text: string | null;
  available_variables: string[];
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface TemplateListResponse {
  templates: TemplateInfo[];
  pagination: PaginationMeta;
}

export interface UpdateTemplateInput {
  template_name?: string;
  subject?: string | null;
  body_html?: string | null;
  body_text?: string | null;
  available_variables?: string[];
  is_active?: boolean;
  is_default?: boolean;
}

export interface RenderedTemplate {
  subject: string | null;
  body_text: string | null;
  body_html: string | null;
}

// -- Company (Wave E) ----------------------------------------------------
export interface CompanyProfile {
  id: number;
  uuid: string;
  name: string;
  slug: string;
  subdomain: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  postcode: string | null;
  website: string | null;
  whatsapp: string | null;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  bank_name: string | null;
  bank_sort_code: string | null;
  bank_account_number: string | null;
  bank_account_name: string | null;
  operating_hours_weekdays: string | null;
  operating_hours_weekends: string | null;
  tier_id: number | null;
  subscription_status: 'trial' | 'active' | 'suspended' | 'cancelled';
  trial_ends_at: string | null;
  subscription_starts_at: string | null;
  subscription_expires_at: string | null;
  status: 'active' | 'inactive' | 'suspended';
  timeframe_am_weekday: string;
  timeframe_am_saturday: string;
  timeframe_ampm_weekday: string;
  timeframe_ampm_saturday: string;
  timeframe_pm_weekday: string;
  timeframe_pm_saturday: string;
  automated_email_reminder_enabled: boolean;
  automated_email_reminder_hours: number;
  automated_email_reminder_time: string;
  automated_sms_reminder_enabled: boolean;
  automated_sms_reminder_hours: number;
  automated_sms_reminder_time: string;
  created_at: string;
  updated_at: string;
}

export interface CollectionsSettingsDto {
  scrap_price_per_tonne: number | null;
  auto_approve_receipts: boolean;
}

export interface CompanyOverviewResponse {
  company: CompanyProfile;
  tier: {
    id: number;
    tier_key: string;
    tier_name: string;
    description: string | null;
    monthly_price: string;
    annual_price: string;
    max_staff: number | null;
    max_drivers: number | null;
    max_monthly_bookings: number | null;
    max_monthly_messages: number | null;
  } | null;
  usage: {
    staff_count: number;
    driver_count: number;
    customer_count: number;
    active_bookings: number;
  };
}

export interface UpdateCompanyInput {
  name?: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  postcode?: string | null;
  website?: string | null;
  whatsapp?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  bank_name?: string | null;
  bank_sort_code?: string | null;
  bank_account_number?: string | null;
  bank_account_name?: string | null;
  operating_hours_weekdays?: string | null;
  operating_hours_weekends?: string | null;
  timeframe_am_weekday?: string;
  timeframe_am_saturday?: string;
  timeframe_ampm_weekday?: string;
  timeframe_ampm_saturday?: string;
  timeframe_pm_weekday?: string;
  timeframe_pm_saturday?: string;
  automated_email_reminder_enabled?: boolean;
  automated_email_reminder_hours?: number;
  automated_email_reminder_time?: string;
  automated_sms_reminder_enabled?: boolean;
  automated_sms_reminder_hours?: number;
  automated_sms_reminder_time?: string;
}

// -- Scrap Quote Generator -----------------------------------------------

// The server now refuses to fabricate a value when neither DVLA nor the
// seed table can identify the vehicle, so the API never returns 'fallback'
// — only verified-data sources reach the client.
export type WeightSource = 'dvla' | 'seed';

export interface GenerateQuoteInput {
  vehicle_reg: string;
  customer_name?: string;
  customer_email?: string;
  price_per_tonne_override?: number;
}

export interface QuoteFoundResponse {
  found: true;
  uuid: string;
  vehicle_reg: string;
  make: string | null;
  model: string | null;
  weight_kg: number;
  weight_source: WeightSource;
  price_per_tonne: number;
  scrap_value: number;
  expires_at: string;
}

export interface QuoteNotFoundResponse {
  found: false;
  vehicle_reg: string;
  reason: 'vehicle_not_identified';
}

export type QuoteResponse = QuoteFoundResponse | QuoteNotFoundResponse;

export interface SendQuoteResult {
  email_queued: boolean;
}

export interface CompanySettingItem {
  id: number;
  setting_key: string;
  setting_value: string | null;
  setting_type: 'text' | 'email' | 'phone' | 'url' | 'textarea' | 'number' | 'boolean' | 'json';
  description: string | null;
  is_secret: boolean;
  created_at: string;
  updated_at: string;
  updated_by: number | null;
}

// -- Reference-data (Wave E) ---------------------------------------------
export interface ReferenceItem {
  id: number;
  uuid: string;
  name: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
}

export interface ReferenceListResponse {
  items: ReferenceItem[];
}

export interface CreateReferenceItemInput {
  name: string;
  description?: string | null;
  display_order?: number;
  is_active?: boolean;
}

export interface UpdateReferenceItemInput {
  name?: string;
  description?: string | null;
  display_order?: number;
  is_active?: boolean;
}

// -- Time slots (Wave E) -------------------------------------------------
export interface TimeSlotDto {
  id: number;
  slot_type: 'weekday' | 'weekend';
  label: string;
  display_order: number;
  is_active: boolean;
}

export interface TimeSlotListResponse {
  items: TimeSlotDto[];
}

export interface CreateTimeSlotInput {
  slot_type: 'weekday' | 'weekend';
  label: string;
  display_order: number;
}

export interface UpdateTimeSlotInput {
  label?: string;
  display_order?: number;
  is_active?: boolean;
}

// -- Communication settings (email / SMS / prefs / parsers) ----------------
// Mirrors server/src/modules/settings/communication/communication.types.ts.
// Passwords are always masked on the server response — '••••••••' if set,
// null otherwise. PUT requires the full credential payload (server validates
// each field as required).

export type EmailPurpose = 'notifications' | 'receipts' | 'invoices' | 'import';
export type SmtpEncryption = 'ssl' | 'tls' | 'none';
export type SmsProvider = 'clicksend' | 'twilio' | 'vonage';

export interface EmailSettings {
  company_id: number;
  purpose: EmailPurpose;
  /**
   * `true` when a row exists for this purpose, `false` when not yet
   * configured. Server returns 200 with `configured: false` (not 404)
   * for unconfigured purposes — see EmailAccountCard's `notConfigured`
   * derivation.
   */
  configured: boolean;
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_username: string | null;
  /** Always masked. '••••••••' if set, null otherwise. */
  smtp_password: string | null;
  smtp_from_name: string | null;
  smtp_from_email: string | null;
  smtp_encryption: SmtpEncryption | null;
  imap_host: string | null;
  imap_port: number | null;
  imap_username: string | null;
  imap_password: string | null;
  imap_folder: string | null;
  is_active: boolean;
  last_tested_at: string | null;
  last_test_result: 'success' | 'failure' | null;
  last_test_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpsertSmtpEmailInput {
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  /** Send the new password OR `null` to keep the existing ciphertext on
   *  the server. Required for new configs (server returns 400 otherwise). */
  smtp_password?: string | null;
  smtp_from_name: string;
  smtp_from_email: string;
  smtp_encryption: SmtpEncryption;
  is_active?: boolean;
}

export interface UpsertImapEmailInput {
  imap_host: string;
  imap_port: number;
  imap_username: string;
  /** See `UpsertSmtpEmailInput.smtp_password`. */
  imap_password?: string | null;
  imap_folder: string;
  is_active?: boolean;
}

export interface TestEmailInput {
  /** Required for outbound (notifications/receipts/invoices); optional for IMAP. */
  test_email?: string;
}

export interface TestResult {
  success: boolean;
  message: string;
}

export interface SmsSettings {
  company_id: number;
  provider: SmsProvider;
  api_username: string | null;
  /** Masked. */
  api_key: string | null;
  api_secret: string | null;
  from_number: string | null;
  is_active: boolean;
  last_tested_at: string | null;
  last_test_result: 'success' | 'failure' | null;
  last_test_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpsertSmsInput {
  provider: SmsProvider;
  api_username?: string | null;
  api_key?: string | null;
  api_secret?: string | null;
  from_number: string;
  is_active?: boolean;
}

export interface TestSmsInput {
  test_number: string;
  provider: SmsProvider;
}

export type NotificationCategory =
  | 'booking'
  | 'receipt'
  | 'invoice'
  | 'cancellation'
  | 'bacs';
export type NotificationChannel = 'email' | 'sms';

export interface NotificationPreference {
  category: NotificationCategory;
  channel: NotificationChannel;
  enabled: boolean;
  email_purpose: 'notifications' | 'receipts' | 'invoices' | null;
}

export interface PreferencesResponse {
  preferences: NotificationPreference[];
}

export interface UpsertPreferencesInput {
  preferences: NotificationPreference[];
}

export interface ImportParser {
  id: number;
  /** null = global parser available to every tenant. */
  company_id: number | null;
  name: string;
  source_identifier: string;
  trigger_subject: string | null;
  trigger_body: string | null;
  is_active: boolean;
  /** 'global' when company_id IS NULL, 'company' when tenant-specific. */
  scope: 'global' | 'company';
  created_at: string;
}

export interface ImportParsersResponse {
  parsers: ImportParser[];
}

// ============================================================================
// Stage 5 — Skip Management module
// ============================================================================

export type SkipJobStatus =
  | 'new'
  | 'in_progress'
  | 'delivered'
  | 'awaiting_collection'
  | 'collected'
  | 'completed'
  | 'cancelled';

export type SkipType = 'skip' | 'bin' | 'roro' | 'other';

/**
 * Legacy enum-like union of `skip_jobs.job_type` values. Renamed from
 * `SkipJobType` (now reserved for the catalogue row interface introduced
 * with skip_job_types) to `SkipJobTypeKey` so both can coexist during the
 * dual-write transition.
 */
export type SkipJobTypeKey =
  | 'scrap_collection'
  | 'delivery_only'
  | 'exchange'
  | 'tip_and_return'
  | 'collection_only'
  | 'load_on_site';

export type SkipPaymentStatus = 'unpaid' | 'paid' | 'partial';

export interface SkipJobSummary {
  enc_id: string;
  uuid: string;
  job_number: string;
  status: SkipJobStatus;
  skip_type: SkipType;
  job_type: SkipJobTypeKey;
  customer_uuid: string | null;
  customer_name: string;
  customer_phone: string;
  customer_postcode: string;
  /** enc_id of the bound customer_sites row. Null when the job has
   *  no saved site (one-off address mode). Drives SitePicker
   *  pre-fill on SkipJobEditPage. NOTE: enc_id is non-deterministic
   *  — match on site_uuid below for cross-response comparisons. */
  site_enc_id: string | null;
  /** Stable uuid of the bound customer_sites row. */
  site_uuid: string | null;
  /** Snapshot of the bound site's address fields (LEFT JOIN). Null
   *  when no site is assigned. */
  site_address_line1: string | null;
  site_address_line2: string | null;
  site_city: string | null;
  site_postcode: string | null;
  site_label: string | null;
  site_is_primary: boolean | null;
  /** Resolved stock type name (from skip_size → skip_stock_types).
   *  Display this in preference to `skip_type` so custom-type
   *  jobs render their real name instead of the legacy ENUM
   *  bucket ('other'). Null when the job has no size set. */
  skip_stock_type_name: string | null;
  skip_stock_type_uuid: string | null;
  driver_uuid: string | null;
  driver_name: string | null;
  skip_inventory_uuid: string | null;
  skip_number: string | null;
  skip_size_id: number | null;
  /** Encrypted enc_id of the bound skip_stock_sizes row. The form
   *  pages bind to this string instead of the integer id (the
   *  /reference/skip-sizes catalogue returns enc_ids). */
  skip_size_enc_id: string | null;
  delivery_date: string | null;
  delivery_time: string | null;
  delivery_status: string;
  collection_date: string | null;
  collection_time: string | null;
  collection_status: string;
  /** Workflow flags exposed on JobSummary in §7.1 — drive client action
   *  buttons without needing the detail fetch. Values: not_requested |
   *  requested | completed (collection adds 'collected'). */
  exchange_status: string;
  tip_return_status: string;
  /** Priority boolean — populated by the server's JobSummary mapper. */
  is_priority: boolean;
  priority_notes: string | null;
  price: number | null;
  payment_status: SkipPaymentStatus;
  created_at: string;
  updated_at: string;
}

export interface SkipJob extends SkipJobSummary {
  customer_email: string | null;
  customer_phone2: string | null;
  job_address_line1: string | null;
  job_address_line2: string | null;
  job_city: string | null;
  job_county: string | null;
  job_postcode: string | null;
  use_different_job_address: boolean;
  notes: string | null;
  internal_notes: string | null;
  delivered_at: string | null;
  collected_at: string | null;
  completed_at: string | null;
  cancellation_reason: string | null;
  payment_method: string | null;
}

export interface SkipJobListResponse {
  jobs: SkipJobSummary[];
  pagination: PaginationMeta;
}

export interface SkipJobNote {
  id: number;
  note: string;
  created_by_name: string | null;
  created_at: string;
}

/**
 * One row of the response from
 * GET /api/v1/skip/jobs/:enc_id/available-actions (Phase 2b).
 * The detail page renders these as buttons; client maps action_key →
 * the matching modal/endpoint.
 */
export interface AvailableJobAction {
  action_key: string;
  button_label: string;
  modal_title: string | null;
  modal_body: string | null;
  dual_submit: boolean;
  is_primary: boolean;
  sort_order: number;
  to_stage_key: string | null;
  spawns_child_type_key: string | null;
  payload_schema_json: Record<string, unknown> | null;
}

export interface SkipJobAttachment {
  enc_id: string;
  uuid: string;
  file_key: string;
  original_filename: string;
  file_size: number;
  file_type: string;
  presigned_url: string | null;
  uploaded_at: string;
  uploaded_by_name: string | null;
}

/** Skip Module v2 — same shape as SkipJobAttachment but bound to
 *  an enquiry. The presigned URL expires in 1h; the list is
 *  refetched on focus to keep links live. */
export interface SkipEnquiryAttachment {
  enc_id: string;
  uuid: string;
  file_key: string;
  original_filename: string;
  file_size: number;
  file_type: string;
  presigned_url: string | null;
  uploaded_at: string;
  uploaded_by_name: string | null;
}

/**
 * Job-action verbs (§7.1). Server endpoints not yet built; the union is
 * declared so workflow buttons can use a typed action prop.
 */
export type SkipJobAction =
  | 'collection-request'
  | 'exchange-request'
  | 'tip-return-request'
  | 'postpone'
  | 'complete-collection'
  | 'complete-exchange'
  | 'complete-tip-return';

// -- Enquiries -------------------------------------------------------------

export type SkipEnquiryStatus =
  | 'open'
  | 'contacted'
  | 'no_answer'
  | 'quoted'
  | 'converted'
  | 'cancelled'
  | 'expired';

export interface SkipEnquirySummary {
  enc_id: string;
  uuid: string;
  enquiry_number: string;
  status: SkipEnquiryStatus;
  is_priority: boolean;
  customer_uuid: string | null;
  customer_name: string;
  customer_phone: string;
  customer_postcode: string;
  /** enc_id of the bound customer_sites row. Null when the enquiry
   *  has no saved site (one-off address mode or un-set). Drives the
   *  SitePicker pre-fill on ProcessEnquiryPage / SkipEnquiryEditPage.
   *  NOTE: enc_id is non-deterministic — use site_uuid for matching. */
  site_enc_id: string | null;
  /** Stable uuid of the bound customer_sites row. */
  site_uuid: string | null;
  /** Snapshot of the bound site's address — mirror columns from the
   *  customer_sites JOIN. Null when no site is assigned. */
  site_address_line1: string | null;
  site_address_line2: string | null;
  site_city: string | null;
  site_postcode: string | null;
  site_label: string | null;
  site_is_primary: boolean | null;
  skip_type: SkipType | null;
  /** Resolved stock type name (from size → stock_types JOIN).
   *  Detail/list pages display this in preference to skip_type. */
  skip_stock_type_name: string | null;
  skip_stock_type_uuid: string | null;
  /** Bug 4: numeric skip_stock_sizes.id when the enquiry has a chosen
   *  size; null otherwise. Drives the inventory filter on
   *  ProcessEnquiryPage so the unit list shows only matching skips. */
  skip_size_id: number | null;
  /** Encrypted enc_id of the bound skip_stock_sizes row (round-trips
   *  the user's selection without exposing the integer id). */
  skip_size_enc_id: string | null;
  job_type: SkipJobTypeKey | null;
  /** Encrypted enc_id of the skip_job_types row. Non-deterministic — do
   *  not use as a cache key; use uuid for that. Drives the job-type
   *  dropdown round-trip on SkipEnquiryEditPage. */
  skip_job_type_enc_id: string | null;
  /** Configured display name for the job type ("Delivery Only"). Null
   *  when no job type is linked. */
  skip_job_type_name: string | null;
  estimated_price: number | null;
  preferred_delivery_date: string | null;
  follow_up_date: string | null;
  contact_attempts: number;
  last_contact_date: string | null;
  converted_skip_job_uuid: string | null;
  created_at: string;
  updated_at: string;
}

export interface SkipEnquiry extends SkipEnquirySummary {
  customer_email: string | null;
  customer_phone2: string | null;
  customer_address_line1: string | null;
  customer_address_line2: string | null;
  customer_city: string | null;
  customer_county: string | null;
  priority_notes: string | null;
  notes: string | null;
  converted_at: string | null;
  cancellation_reason: string | null;
  cancelled_at: string | null;
}

export interface SkipEnquiryListResponse {
  enquiries: SkipEnquirySummary[];
  pagination: PaginationMeta;
}

/**
 * Contact-log entries (§7.2). Server endpoints not yet built.
 */
export interface SkipEnquiryContact {
  id: number;
  contact_method: 'phone' | 'email' | 'sms' | 'in_person' | 'other';
  outcome: 'answered' | 'no_answer' | 'voicemail' | 'emailed' | 'texted' | 'other';
  notes: string | null;
  contacted_at: string;
  contacted_by_name: string | null;
}

// -- Inventory -------------------------------------------------------------

export type SkipInventoryStatus = 'available' | 'out' | 'maintenance';
export type SkipCondition = 'excellent' | 'good' | 'fair' | 'poor' | 'repair_required';

export interface SkipInventorySummary {
  enc_id: string;
  uuid: string;
  skip_number: string;
  /** enc_id of the bound skip_stock_sizes row. */
  skip_size_enc_id: string | null;
  /** enc_id of the bound skip_stock_types row. */
  skip_stock_type_enc_id: string | null;
  /** Denormalised prefix from the stock type (SKP / BIN / ROR …). */
  unit_prefix: string | null;
  stock_type_name: string | null;
  size_name: string | null;
  status: SkipInventoryStatus;
  effective_status: SkipInventoryStatus;
  skip_condition: SkipCondition;
  current_location: string | null;
  active_job_uuid: string | null;
  active_job_number: string | null;
  /** ISO timestamp of the binding job's delivered_at — drives the
   *  client's "days out" badge per §7.9. */
  active_job_delivered_at: string | null;
  /** True once the unit has ever been bound to any job — disables hard delete. */
  has_had_job: boolean;
  created_at: string;
  updated_at: string;
}

export interface SkipInventoryItem extends SkipInventorySummary {
  notes: string | null;
  last_service_date: string | null;
  next_service_date: string | null;
}

export interface SkipInventoryListResponse {
  inventory: SkipInventorySummary[];
  pagination: PaginationMeta;
}

// -- Reference / sites -----------------------------------------------------

export interface SkipSize {
  id: string;
  uuid: string;
  skip_type: 'skip' | 'bin' | 'roro';
  name: string;
  description: string | null;
  typical_capacity: string | null;
  price_delivery: number | null;
  price_collection: number | null;
  is_active: boolean;
  display_order: number;
  /** Bug 2 — enc_id of the parent skip_stock_types row. Lets the form
   *  size dropdown filter by stock type when a custom type is chosen
   *  (custom types are bucketed under skip_type='skip' in the flat
   *  list because the legacy enum has no slot for them).
   *
   *  NOTE: enc_id is non-deterministic (random IV per encryptField
   *  call), so two responses for the same row produce different
   *  ciphertexts. Form code MUST match by stock_type_uuid below
   *  instead — that is stable. */
  stock_type_enc_id: string;
  /** Stable uuid of the parent skip_stock_types row. Use this for
   *  cross-response matching (Bug 2 follow-up) — comparing
   *  stock_type_enc_id strings between two API responses fails
   *  because the encryption is non-deterministic. */
  stock_type_uuid: string;
  stock_type_name: string;
}

// -- Stock types + sizes (post skip_sizes migration) -----------------------

export interface SkipStockType {
  enc_id: string;
  uuid: string;
  name: string;
  prefix: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
  sizes_count: number;
  /** True when this is a global default served to every tenant. The UI
   *  hides edit/delete and shows a "Default" badge; the server returns
   *  403 on any modification attempt. */
  is_global: boolean;
  /** Only present on globals (is_global === true). True when the calling
   *  tenant has opted-in via POST /skip/stock/types/globals/:enc_id/activate.
   *  Globals are off by default for new tenants — deactivated globals
   *  are hidden from inventory create/edit selectors but still rendered
   *  (greyed) on the SkipStockTypesPage. */
  activated?: boolean;
  created_at: string;
  updated_at: string;
}

export interface SkipStockSize {
  enc_id: string;
  uuid: string;
  name: string;
  description: string | null;
  typical_capacity: string | null;
  is_active: boolean;
  sort_order: number;
  /** Mirrors SkipStockType.is_global — true for sizes belonging to a
   *  global stock type. */
  is_global: boolean;
  created_at: string;
  updated_at: string;
}

export interface SkipStockTypeWithSizes extends SkipStockType {
  sizes: SkipStockSize[];
}

export interface SkipStockTypeForReference {
  enc_id: string;
  uuid: string;
  name: string;
  prefix: string;
  sizes: ReadonlyArray<{
    enc_id: string;
    uuid: string;
    name: string;
    description: string | null;
    typical_capacity: string | null;
  }>;
}

export interface CreateSkipStockTypeInput {
  name: string;
  prefix: string;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

export interface UpdateSkipStockTypeInput {
  name?: string;
  description?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

export interface CreateSkipStockSizeInput {
  name: string;
  description?: string | null;
  typical_capacity?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

export interface UpdateSkipStockSizeInput {
  name?: string;
  description?: string | null;
  typical_capacity?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

export interface CustomerSite {
  id: string;
  uuid: string;
  enc_id?: string;
  site_name: string;
  address: string;
  address_line1: string;
  address_line2: string | null;
  city: string | null;
  county: string | null;
  postcode: string;
  country: string;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  is_primary: boolean;
  is_active: boolean;
}

// -- Skip job types + stages + actions (Phase 1+ rebuild) ----------------
//
// Mirrors SkipStockType. is_global=true → server-managed default; the UI
// hides edit/delete and shows "Default" badge. is_child_only=true →
// system-generated child jobs only (exchange / tip_and_return); the UI
// hides them from job-create dropdowns and shows a "System" badge in
// the settings list.

export interface SkipJobType {
  enc_id: string;
  uuid: string;
  key: string;
  name: string;
  prefix: string;
  description: string | null;
  process_immediately: boolean;
  is_child_only: boolean;
  allows_priority: boolean;
  is_active: boolean;
  sort_order: number;
  has_had_first_use: boolean;
  is_global: boolean;
  /** Only present on globals (is_global === true). */
  activated?: boolean;
  stages_count: number;
  actions_count: number;
  created_at: string;
  updated_at: string;
}

export interface SkipJobTypeStage {
  enc_id: string;
  uuid: string;
  key: string;
  label: string;
  stage_order: number;
  is_start: boolean;
  is_terminal: boolean;
  triggers_dispatch: boolean;
  /** UUID of another stage (in the same job type) the server should
   *  immediately advance to on entry. Used by the process_immediately
   *  path so scrap_collection.new auto-advances to .completed. */
  auto_advance_to_stage_uuid: string | null;
  is_active: boolean;
  is_global: boolean;
  created_at: string;
  updated_at: string;
}

export interface SkipJobTypeAction {
  enc_id: string;
  uuid: string;
  action_key: string;
  button_label: string;
  modal_title: string | null;
  modal_body: string | null;
  /** UUID of the stage from which this action is available. NULL =
   *  available from any non-terminal stage (e.g. cancel). */
  from_stage_uuid: string | null;
  /** UUID of the stage the job moves to on success. NULL = no stage
   *  change (e.g. request_collection just sets a sub-flag). */
  to_stage_uuid: string | null;
  /** When set, the action also creates a child job of this type. */
  spawns_child_type_uuid: string | null;
  dual_submit: boolean;
  payload_schema_json: Record<string, unknown> | null;
  is_primary: boolean;
  sort_order: number;
  is_active: boolean;
  is_global: boolean;
  created_at: string;
  updated_at: string;
}

export interface SkipJobTypeWithChildren extends SkipJobType {
  stages: SkipJobTypeStage[];
  actions: SkipJobTypeAction[];
}

export interface CreateSkipJobTypeInput {
  key: string;
  name: string;
  prefix: string;
  description?: string | null;
  process_immediately?: boolean;
  is_child_only?: boolean;
  allows_priority?: boolean;
  sort_order?: number;
  is_active?: boolean;
}

export interface UpdateSkipJobTypeInput {
  name?: string;
  description?: string | null;
  process_immediately?: boolean;
  is_child_only?: boolean;
  allows_priority?: boolean;
  is_active?: boolean;
  sort_order?: number;
}

export interface CreateSkipJobTypeStageInput {
  key: string;
  label: string;
  stage_order: number;
  is_start?: boolean;
  is_terminal?: boolean;
  triggers_dispatch?: boolean;
  is_active?: boolean;
  auto_advance_to_stage_uuid?: string | null;
}

export interface UpdateSkipJobTypeStageInput {
  label?: string;
  stage_order?: number;
  is_start?: boolean;
  is_terminal?: boolean;
  triggers_dispatch?: boolean;
  is_active?: boolean;
  auto_advance_to_stage_uuid?: string | null;
}

export interface CreateSkipJobTypeActionInput {
  action_key: string;
  button_label: string;
  modal_title?: string | null;
  modal_body?: string | null;
  from_stage_uuid?: string | null;
  to_stage_uuid?: string | null;
  spawns_child_type_uuid?: string | null;
  dual_submit?: boolean;
  payload_schema_json?: Record<string, unknown> | null;
  is_primary?: boolean;
  sort_order?: number;
  is_active?: boolean;
}

export interface UpdateSkipJobTypeActionInput {
  button_label?: string;
  modal_title?: string | null;
  modal_body?: string | null;
  from_stage_uuid?: string | null;
  to_stage_uuid?: string | null;
  spawns_child_type_uuid?: string | null;
  dual_submit?: boolean;
  payload_schema_json?: Record<string, unknown> | null;
  is_primary?: boolean;
  sort_order?: number;
  is_active?: boolean;
}

// -- Print templates (PRINT_TEMPLATE_PLAN.md §4) -------------------------
//
// Mirrors `server/src/modules/printTemplates/printTemplates.types.ts`.
// Keep these shapes in lockstep with the server — the renderer uses
// the structural Zod schema defined client-side at
// `clients/web/src/pages/settings/print-templates/validation.ts`.

export type PrintTemplateType =
  | 'diary_results'
  | 'receipt'
  | 'invoice'
  | 'wb_ticket'
  | 'wb_advice_note'
  | 'wb_wtn'
  | 'wb_hazardous_consignment';

export interface ColumnDef {
  key: string;
  label: string;
  /** 1..60. Sum of width_pct across all columns must be ≤ 100. */
  width_pct: number;
  align: 'left' | 'center' | 'right';
  format?: 'plain' | 'currency' | 'date_short';
}

export interface HeaderConfig {
  show: boolean;
  logo: { show: boolean; max_height_mm: number };
  /** Free text with `{{token}}` placeholders. May be empty. */
  title_template: string;
  subtitle_template: string;
  /** 0..6 lines. Each line is an independent template. */
  company_lines: string[];
  alignment: 'left' | 'center' | 'right';
}

export interface BodyConfig {
  /** 0..12 columns. */
  columns: ColumnDef[];
  show_zebra: boolean;
  /** diary_results only — ignored for other types. */
  group_by_day: boolean;
  /** diary_results only — ignored when group_by_day is false. */
  show_day_subtotals: boolean;
}

export interface FooterConfig {
  show: boolean;
  /** Multi-line; \n preserved. */
  text_template: string;
  show_page_numbers: boolean;
}

export interface PageConfig {
  size: 'A4' | 'A5' | 'Letter';
  orientation: 'portrait' | 'landscape';
  margins_mm: { top: number; right: number; bottom: number; left: number };
}

export type PrintTemplateMode = 'columns' | 'custom_html';

export interface PrintTemplateContent {
  /** Versioned to allow future shape migrations. v1 is the only
   *  currently-accepted value (DECISION-TPL-004). */
  version: 1;
  /** Render mode. Optional — older saved templates without this
   *  field default to `'columns'` on the server (Hybrid Builder
   *  Extension). Sending `'custom_html'` swaps the renderer onto
   *  the free-form HTML + CSS path. */
  mode?: PrintTemplateMode;
  header: HeaderConfig;
  body: BodyConfig;
  footer: FooterConfig;
  page: PageConfig;
  /** Free-form HTML body — used only when mode='custom_html'. */
  custom_html?: string;
  /** CSS injected into the document head — used only when mode='custom_html'. */
  custom_css?: string;
}

export interface PrintTemplateInfo {
  uuid: string;
  template_type: PrintTemplateType;
  template_name: string;
  template_content: PrintTemplateContent;
  content_version: number;
  /** True when the row's company_id matches the caller (tenant
   *  override). False when inheriting the global default. */
  is_custom: boolean;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  /** Optional — server may surface in a future revision. */
  updated_by?: string | null;
}

export interface PrintTemplatesListResponse {
  templates: PrintTemplateInfo[];
}

/** PUT body — `template_name` falls back to a per-type default
 *  when omitted. */
export interface UpsertPrintTemplateInput {
  template_name?: string;
  template_content: PrintTemplateContent;
}

/** POST /:templateType/render body. `context` carries the data the
 *  renderer interpolates against; `sample` swaps in the per-type
 *  fixture (server-side) for live-preview mode. */
export interface RenderPrintTemplateRequest {
  context?: Record<string, unknown>;
  sample?: boolean;
}

// --- Tenant storage configuration (admin-only) ------------------------------
//
// Lets a tenant admin point file storage at their own object store. Server
// module mounts at /api/v1/admin/storage. Credentials (access/secret keys)
// are write-only — the public read shape never returns them.

export type StorageType = 'platform' | 'minio' | 'local';

/** Read shape returned by GET / PUT. Never carries credential values —
 *  only the `has_credentials` flag indicating whether keys are stored. */
export interface TenantStorageConfigPublic {
  storage_type: StorageType;
  endpoint: string | null;
  bucket: string | null;
  use_ssl: boolean;
  region: string;
  public_base_url: string | null;
  has_credentials: boolean;
}

/** Write body for PUT / POST test. `access_key` / `secret_key` are
 *  write-only; omit them to keep the existing stored credentials. */
export interface StorageConfigInput {
  storage_type: StorageType;
  endpoint?: string;
  bucket?: string;
  access_key?: string;
  secret_key?: string;
  use_ssl?: boolean;
  region?: string;
  public_base_url?: string;
}

/** Result of POST /test — does NOT persist anything. */
export interface StorageTestResult {
  ok: boolean;
  error?: string;
}
